import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  MapPin,
  Navigation,
  Clock,
  Info,
  ExternalLink,
  Snowflake,
  Sun
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';

const ContactInfo = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());
  const t = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };

  useEffect(() => {
    try {
      setCurrentLanguage(getStoredLanguage());
    } catch (_) {}
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') window.addEventListener('app-language-change', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('app-language-change', handler); };
  }, []);

  const locations = [
    {
      id: 'bandirma',
      name: 'Bandırma Gemi Müze ve Millî Mücadele Açık Hava Müzesi',
      nameEng: 'Bandırma Ship Museum & National Struggle Open-Air Museum',
      address: 'Belediyeevleri Mah. Doğu Park Mevkii Canik',
      phone: '0 362 238 00 23',
      coordinates: { lat: 41.2867, lng: 36.3300 },
      workingHours: 'Kış Saati: Pazartesi: 12.00-16.45, Diğer Günler: 08.30-16.45 | Yaz Saati: Pazartesi: 12.00-18.45, Diğer Günler: 08.30-18.45',
      workingHoursEng: 'Winter Hours: Monday: 12:00-16:45, Other Days: 08:30-16:45 | Summer Hours: Monday: 12:00-18:45, Other Days: 08:30-18:45',
      category: 'Müze'
    },
    {
      id: 'kent-muzesi',
      name: 'Samsun Kent Müzesi',
      nameEng: 'Samsun City Museum',
      address: 'Zafer Mahallesi Cumhuriyet Caddesi No:37 İlkadım',
      phone: '0362 234 34 54',
      coordinates: { lat: 41.2928, lng: 36.3313 },
      workingHours: 'Kış Saati: Pazartesi: 12.00-16.45, Diğer Günler: 08.30-16.45 | Yaz Saati: Pazartesi: 12.00-17:45, Diğer Günler: 08.30-17:45',
      workingHoursEng: 'Winter Hours: Monday: 12:00-16:45, Other Days: 08:30-16:45 | Summer Hours: Monday: 12:00-17:45, Other Days: 08:30-17:45',
      category: 'Müze'
    },
    {
      id: 'onur-aniti',
      name: 'Onur Anıtı',
      nameEng: 'Onur Monument',
      address: 'Cumhuriyet Caddesi Atatürk Bulvarı İlkadım',
      phone: null,
      coordinates: { lat: 41.2925, lng: 36.3358 },
      workingHours: null,
      workingHoursEng: null,
      category: 'Anıt'
    },
    {
      id: 'tutun-iskelesi',
      name: 'Tütün İskelesi',
      nameEng: 'Tobacco Pier',
      address: 'Kale Mh. Kurtuluş Yolu İlkadım/Samsun',
      phone: null,
      coordinates: { lat: 41.2889, lng: 36.3397 },
      workingHours: null,
      workingHoursEng: null,
      category: 'Tarihi Yapı'
    },
    {
      id: 'alacam-muzesi',
      name: 'Alaçam Mübadele Müzesi',
      nameEng: 'Alaçam Exchange Museum',
      address: 'Çeşme, Eski Şube Sk. No:8, 55800 Alaçam',
      phone: null,
      coordinates: { lat: 41.6167, lng: 35.7500 },
      workingHours: 'Pazartesi: Kapalı, Diğer günler: 8:30-16:45',
      workingHoursEng: 'Closed on Monday, Other days: 8:30-16:45',
      category: 'Müze'
    },
    {
      id: 'amazon-koyu',
      name: 'Amazon Köyü',
      nameEng: 'Amazon Village',
      address: 'Baruthane Mahallesi Atatürk Bulvarı. No: 57 İlkadım/Samsun',
      phone: '0530 157 26 09',
      coordinates: { lat: 41.2917, lng: 36.3306 },
      workingHours: 'Kış saati: Pazartesi: 12:00-16:45, Diğer günler: 8:30-16:45 | Yaz saati: Pazartesi: 12:00-18:45, Diğer günler: 8:30-18:45',
      workingHoursEng: 'Winter Hours: Monday: 12:00-16:45, Other days: 8:30-16:45 | Summer Hours: Monday: 12:00-18:45, Other days: 8:30-18:45',
      category: 'Kültürel Alan'
    },
    {
      id: 'bafra-muzesi',
      name: 'Bafra Tütün Müzesi',
      nameEng: 'Bafra Tobacco Museum',
      address: 'Kızılırmak, Kaymakam Sk. No:2, 55400 Bafra',
      phone: '03625431030',
      coordinates: { lat: 41.5667, lng: 35.9000 },
      workingHours: 'Pazartesi: Kapalı, Diğer günler: 8:30-16:45',
      workingHoursEng: 'Closed on Monday, Other days 8:30-16:45',
      category: 'Müze'
    },
    {
      id: 'gazi-muzesi',
      name: 'Gazi Müzesi',
      nameEng: 'Gazi Museum',
      address: 'Kale, Gazi Cd. No:62, 55030 İlkadım/Samsun',
      phone: '0 (362) 435 75 35',
      coordinates: { lat: 41.2889, lng: 36.3397 },
      workingHours: '8:00-17:00',
      workingHoursEng: '8:00-17:00',
      category: 'Müze'
    },
    {
      id: 'havza-ataturk-evi',
      name: 'Havza Atatürk Evi',
      nameEng: 'Havza Atatürk House',
      address: 'Medrese Mh, Karakol Sk. Havza',
      phone: '0 362 714 13 59',
      coordinates: { lat: 40.9667, lng: 35.6667 },
      workingHours: 'Pazartesi: Kapalı, Diğer günler: 8:30-16:45',
      workingHoursEng: 'Closed on Monday, Other days: 8:30-16:45',
      category: 'Müze'
    },
    {
      id: 'ilkadim-aniti',
      name: 'İlkadım Anıtı',
      nameEng: 'İlkadım Monument',
      address: 'Kale Mh, Atatürk Bl. No:171, 55030 İlkadım',
      phone: null,
      coordinates: { lat: 41.2889, lng: 36.3397 },
      workingHours: null,
      workingHoursEng: null,
      category: 'Anıt'
    },
    {
      id: 'sadi-tekkesi',
      name: 'Sadi Tekkesi Kuva-İ Milliye Ruhu Külliyesi',
      nameEng: 'Sadi Lodge Complex of National Spirit',
      address: 'Selahiye Mahallesi Çifte Hamam Sokak No 57',
      phone: '0362 432 40 00',
      coordinates: { lat: 41.2889, lng: 36.3397 },
      workingHours: '09:00 - 17:00',
      workingHoursEng: '09:00 - 17:00',
      category: 'Kültürel Alan'
    },
    {
      id: 'samsun-muzesi',
      name: 'Samsun Müzesi',
      nameEng: 'Samsun Museum',
      address: 'Liman mah Atatürk bulvarı No:99 İlkadım/ Samsun',
      phone: '0362 431 68 28',
      coordinates: { lat: 41.2889, lng: 36.3397 },
      workingHours: 'Kış saati: 8:00-17:00 | Yaz saati: 8:00-20:00',
      workingHoursEng: 'Winter Hours: 8:00-17:00 | Summer Hours: 8:00-20:00',
      category: 'Müze'
    }
  ];

  const openMaps = (location) => {
    // Önce adres bazlı arama yapalım, daha doğru sonuç verir
    const searchQuery = encodeURIComponent(`${location.name}, ${location.address}`);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;
    window.open(mapsUrl, '_blank');
  };

  const callPhone = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone.replace(/\s/g, '')}`;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Müze':
        return 'bg-blue-100 text-blue-800';
      case 'Anıt':
        return 'bg-green-100 text-green-800';
      case 'Tarihi Yapı':
        return 'bg-purple-100 text-purple-800';
      case 'Kültürel Alan':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const translateCategory = (category) => {
    const mapEn = {
      'Müze': 'Museum',
      'Anıt': 'Monument',
      'Tarihi Yapı': 'Historic Site',
      'Kültürel Alan': 'Cultural Site'
    };
    const mapRu = {
      'Müze': 'Музей',
      'Anıt': 'Памятник',
      'Tarihi Yapı': 'Исторический объект',
      'Kültürel Alan': 'Культурная зона'
    };
    if (currentLanguage === 'EN') return mapEn[category] || category;
    if (currentLanguage === 'RU') return mapRu[category] || mapEn[category] || category;
    return category;
  };

  const formatWorkingHours = (workingHours, workingHoursEng) => {
    if (!workingHours) return null;
    
    const hours = currentLanguage === 'TR' ? workingHours : (workingHoursEng || workingHours);
    
    // Kış ve yaz saatlerini ayır
    if (hours.includes('|') || hours.match(/[Kk]ış|[Ww]inter|[Yy]az|[Ss]ummer/)) {
      const parts = hours.split('|').map(p => p.trim());
      const winterPart = parts.find(p => p.match(/[Kk]ış|[Ww]inter/));
      const summerPart = parts.find(p => p.match(/[Yy]az|[Ss]ummer/));
      
      return {
        winter: winterPart ? winterPart.replace(/[Kk]ış\s*[Ss]aati?:|[Ww]inter\s*[Hh]ours?:/gi, '').trim() : null,
        summer: summerPart ? summerPart.replace(/[Yy]az\s*[Ss]aati?:|[Ss]ummer\s*[Hh]ours?:/gi, '').trim() : null
      };
    }
    
    return { simple: hours };
  };

  const renderFormattedHours = (text) => {
    if (!text) return null;
    
    // Etiketleri bold yapmak için regex pattern'leri
    const patterns = [
      { regex: /(Pazartesi\s*:)/gi },
      { regex: /(Diğer\s+günler\s*:)/gi },
      { regex: /(Diğer\s+Günler\s*:)/gi },
      { regex: /(Monday\s*:)/gi },
      { regex: /(Other\s+Days\s*:)/gi },
      { regex: /(Other\s+days\s*:)/gi },
      { regex: /(Pazartesi\s+kapalı)/gi },
      { regex: /(Closed\s+on\s+Monday)/gi }
    ];
    
    // Metni virgülle ayır ve her bir kısmı işle
    const segments = text.split(',').map(s => s.trim()).filter(s => s.length > 0);
    
    return (
      <div className="text-xs text-gray-700 leading-relaxed space-y-0.5">
        {segments.map((segment, segmentIndex) => {
          // Bu segment içindeki etiketleri bul
          let labelMatch = null;
          let labelText = '';
          let hoursText = '';
          
          // İlk eşleşen etiketi bul
          for (const { regex } of patterns) {
            const match = regex.exec(segment);
            if (match) {
              labelMatch = match;
              labelText = match[0];
              hoursText = segment.substring(match.index + match[0].length).trim();
              break;
            }
          }
          
          // Eğer etiket bulunamadıysa, tüm metni saat olarak kabul et
          if (!labelMatch) {
            hoursText = segment;
          }
          
          return (
            <div key={segmentIndex} className="space-y-0.5">
              {labelText && (
                <div className="font-semibold">{labelText}</div>
              )}
              {hoursText && (
                <div>{hoursText}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">{t('Ulaşım ve İletişim', 'Transport & Contact', 'Транспорт и связь')}</h1>
                <p className="text-sm text-gray-600">{t('Müze ve tarihi mekanlar', 'Museums and historic sites', 'Музеи и исторические объекты')}</p>
              </div>
            </div>
            <div className="flex items-center">
              <LanguageSelector size="sm" onChange={setCurrentLanguage} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start space-x-3">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-blue-900 mb-2">{t('Ziyaret Bilgileri', 'Visitor Information', 'Информация для посетителей')}</h2>
              <p className="text-blue-800 leading-relaxed">
                {t(
                  'Müzelerimizi ziyaret etmeden önce çalışma saatlerini kontrol etmenizi öneririz. Bazı müzeler özel günlerde kapalı olabilir veya farklı saatlerde hizmet verebilir.',
                  'Please check opening hours before your visit. Some museums may be closed on special days or operate at different hours.',
                  'Пожалуйста, проверяйте часы работы перед визитом. Некоторые музеи могут быть закрыты в особые дни или работать по изменённому графику.'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <div
              key={location.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col h-full min-h-[400px]"
            >
              {/* Header - Fixed Height */}
              <div className="p-6 border-b border-gray-100 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-bold text-gray-900 leading-tight line-clamp-3 flex-1 mr-3">
                    {currentLanguage === 'TR' ? location.name : (location.nameEng || location.name)}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getCategoryColor(location.category)}`}>
                    {currentLanguage === 'TR' ? location.category : translateCategory(location.category)}
                  </span>
                </div>
              </div>

              {/* Content - Flexible Height */}
              <div className="p-6 space-y-4 flex-1">
                {/* Address */}
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-1">{t('Adres', 'Address', 'Адрес')}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{location.address}</p>
                  </div>
                </div>

                {/* Phone - Fixed Height */}
                <div className="flex items-start space-x-3 min-h-[50px]">
                  <Phone className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-1">{t('Telefon', 'Phone', 'Телефон')}</p>
                    {location.phone ? (
                      <button
                        onClick={() => callPhone(location.phone)}
                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {location.phone}
                      </button>
                    ) : (
                      <p className="text-sm text-gray-400">{t('Bilgi yok', 'No info', 'Нет данных')}</p>
                    )}
                  </div>
                </div>

                {/* Working Hours */}
                {location.workingHours && (
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-2">{t('Çalışma Saatleri', 'Opening Hours', 'Часы работы')}</p>
                      {(() => {
                        const formatted = formatWorkingHours(location.workingHours, location.workingHoursEng);
                        if (!formatted) return null;
                        
                        if (formatted.simple) {
                          return (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                              {renderFormattedHours(formatted.simple)}
                            </div>
                          );
                        }
                        
                        // Kış ve yaz kutucuklarını kontrol et
                        const hasBoth = formatted.winter && formatted.summer;
                        const hasOnlyOne = (formatted.winter && !formatted.summer) || (!formatted.winter && formatted.summer);
                        
                        return (
                          <div className={`flex gap-2.5 ${hasBoth ? 'flex-col sm:flex-row' : 'justify-center'}`}>
                            {formatted.winter && (
                              <div className={`bg-blue-50 border border-blue-100 rounded-lg p-2.5 ${hasOnlyOne ? 'w-full sm:w-auto sm:min-w-[200px]' : 'flex-1'}`}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Snowflake className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                  <p className="text-xs font-semibold text-blue-900">{t('Kış', 'Winter', 'Зима')}</p>
                                </div>
                                {renderFormattedHours(formatted.winter)}
                              </div>
                            )}
                            {formatted.summer && (
                              <div className={`bg-orange-50 border border-orange-100 rounded-lg p-2.5 ${hasOnlyOne ? 'w-full sm:w-auto sm:min-w-[200px]' : 'flex-1'}`}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Sun className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />
                                  <p className="text-xs font-semibold text-orange-900">{t('Yaz', 'Summer', 'Лето')}</p>
                                </div>
                                {renderFormattedHours(formatted.summer)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions - Fixed Position at Bottom */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => openMaps(location)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                >
                  <Navigation className="w-4 h-4" />
                  <span className="font-medium">{t('Yol Tarifi Al', 'Get Directions', 'Построить маршрут')}</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('Genel Bilgiler', 'General Information', 'Общая информация')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Ziyaret Önerileri', 'Visiting Tips', 'Советы для посещения')}</h3>
              <ul className="space-y-2 text-gray-600">
                <li>{t('• Müze ziyaretlerinizi haftaiçi planlamanızı öneririz.', '• We recommend planning your museum visits on weekdays.', '• Рекомендуем планировать посещение музеев в будние дни.')}</li>
                <li>{t('• Grup ziyaretleri için rezervasyon yaptırınız.', '• Make a reservation for group visits.', '• Бронируйте для групповых посещений.')}</li>
                <li>{t('• Mekânlarda fotoğraf ve video çekimi için izin gerekli olabileceğini unutmayınız.', '• Please note that permission may be required for photography and video recording at venues.', '• Помните, что для фото и видеосъёмки в местах может потребоваться разрешение.')}</li>
                <li>{t('• Rehber talebiniz varsa müze ile iletişime geçiniz.', '• If you need a guide, please contact the museum.', '• Если вам нужен гид, свяжитесь с музеем.')}</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Ulaşım ve Lokasyon Önerileri', 'Transportation and Location Recommendations', 'Рекомендации по транспорту и местоположению')}</h3>
              <ul className="space-y-2 text-gray-600">
                <li>{t('• Şehir merkezindeki müzelere toplu taşıma araçlarıyla ulaşabilirsiniz.', '• You can reach museums in the city center by public transportation.', '• До музеев в центре города можно добраться на общественном транспорте.')}</li>
                <li>{t('• İlçe müzeleri için özel araç önerilmektedir.', '• Private vehicle is recommended for district museums.', '• Для районных музеев рекомендуется личный автомобиль.')}</li>
                <li>{t('• Müze çevresinde otopark imkânı mevcuttur.', '• Parking facilities are available around the museums.', '• Вокруг музеев есть парковки.')}</li>
                <li>{t('• Engelli erişimi bulunan müzeler belirtilmiştir.', '• Museums with disabled access are indicated.', '• Музеи с доступом для инвалидов указаны.')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
